import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { useEffect, useState } from "react";
import { DndProvider } from "react-dnd-multi-backend";
import "./App.css";
import ChooseSystemPopup from "./components/ChooseSystemPopup";
import { DraggableLegend, DraggableLevelUnlocked, DraggableTutorial } from "./components/Draggables";
import InformationPopup from "./components/InformationPopup";
import Introduction from "./components/Introduction";
import NewGamePopup from "./components/NewGamePopup";
import {
  BINARY_NUMBER_OF_LEVELS,
  BINARY_SYSTEM,
  COLOR_LIGHT, FONT,
  MAYA_NUMBER_OF_LEVELS,
  MAYA_SYSTEM,
  ROMAN_NUMBER_OF_LEVELS,
  ROMAN_SYSTEM
} from "./defaults";
import arrowLeft from "./images/arrow_left.png";
import arrowRight from "./images/arrow_right.png";
import mascot_computer from "./images/mascot_computer.png";
import mascot_maya from "./images/mascot_maya.png";
import mascot_roman from "./images/mascot_roman.png";
import Level from "./level";
import Navbar from "./navbar";
import { AppRotateScreen, AppScreenSize, buttonChangeSystem, buttonShowLegend, buttonShowTutorial, lang } from "./strings";

/**
 * Take value out of storage, read it, put it back. If value is null, return initialValue. Storage persistent between reloads, similar to cookie.
 * @param {*} key 
 * @param {*} initialValue 
 * @returns Stored value or initialValue if nothing stored for key, and setFunction for that value. Array-form.
 */
const useLocalStorage = (key, initialValue) => {
  const [value, setValue] = useState(() => {
    const storedValue = localStorage.getItem(key);
    return storedValue !== null ? JSON.parse(storedValue) : initialValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [parseInt(value), setValue];
};

const useLocalStorageProgress = (key, initialValue, numberOfLevels) => {
  const [value, setValue] = useState(() => {
    const storedValue = localStorage.getItem(key);
    if (
      storedValue !== null &&
      JSON.parse(storedValue).map(Number).length < numberOfLevels
    ) {
      return initialValue;
    }
    return storedValue !== null
      ? JSON.parse(storedValue).map(Number)
      : initialValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
};

function App() {
  const [level, setLevel] = useLocalStorage("level", 0); // Which level is currently loaded. Numeral system-agnostic.
  const [whichSystem, setWhichSystem] = useLocalStorage("whichSystem",ROMAN_SYSTEM);
  const [disableInput, setDisableInput] = useState(false);
  const [romanProgress, setRomanProgress] = useLocalStorage("romanProgress", 0);
  const [binaryProgress, setBinaryProgress] = useLocalStorage("binaryProgress", 0);
  const [mayaProgress, setMayaProgress] = useLocalStorage("mayaProgress", 0);
  const [romanLevelProgress, setRomanLevelProgress] = useLocalStorageProgress(
    "romanLevelProgress",
    Array(ROMAN_NUMBER_OF_LEVELS).fill(0),
    ROMAN_NUMBER_OF_LEVELS
  );
  const [binaryLevelProgress, setBinaryLevelProgress] = useLocalStorageProgress(
    "binaryLevelProgress",
    Array(BINARY_NUMBER_OF_LEVELS).fill(0),
    BINARY_NUMBER_OF_LEVELS
  );
  const [mayaLevelProgress, setMayaLevelProgress] = useLocalStorageProgress(
    "mayaLevelProgress",
    Array(MAYA_NUMBER_OF_LEVELS).fill(0),
    MAYA_NUMBER_OF_LEVELS
  );
  const [romanTutorialProgress, setRomanTutorialProgress] = useLocalStorageProgress(
    "romanTutorialProgress",
    Array(ROMAN_NUMBER_OF_LEVELS).fill(0)
  );
  const [binaryTutorialProgress, setBinaryTutorialProgress] = useLocalStorageProgress(
    "binaryTutorialProgress",
    Array(BINARY_NUMBER_OF_LEVELS).fill(0)
  );
  const [mayaTutorialProgress, setMayaTutorialProgress] = useLocalStorageProgress(
    "mayaTutorialProgress",
    Array(MAYA_NUMBER_OF_LEVELS).fill(0)
  );
 

  const [isScreenSmall, setIsScreenSmall] = useState(false);
  const [isScreenPortrait, setIsScreenPortrait] = useState(false);

  const checkScreenSize = () => {
    if (window.innerWidth < 600 || window.innerHeight < 360) {
      setIsScreenSmall(true);
    } else {
      setIsScreenSmall(false);
    }
    if (window.matchMedia('(orientation: portrait)').matches) {
      setIsScreenPortrait(true);
    } else {
      setIsScreenPortrait(false);
    }
  };

  /**
   * 
   * @param {Number} whichSystem 
   * @returns Number indicating the highest unlocked level of the chosen system.
   */
  const progress = (whichSystem) => {
    switch (whichSystem) {
      case ROMAN_SYSTEM:
        return romanProgress;
      case BINARY_SYSTEM:
        return binaryProgress;
      case MAYA_SYSTEM:
        return mayaProgress;
      default:
        console.log("progress: invalid system code passed to progress()!");
    }
  };

  const setProgress = (whichSystem, newProgress) => {
    switch (whichSystem) {
      case ROMAN_SYSTEM:
        setRomanProgress(newProgress);
        break;
      case BINARY_SYSTEM:
        setBinaryProgress(newProgress);
        break;
      case MAYA_SYSTEM:
        setMayaProgress(newProgress);
        break;
      default:
        console.log("setProgress: invalid system code passed to setProgress()!");
    }
  };

  /**
   * 
   * @param {Number} whichSystem 
   * @returns Array containing either 0 or 1 for each level of the chosen system. 1 indicates that the tutorial for the respective level has been completed, 0 indicates that it has not. 
   */
  const tutorialProgress = (whichSystem) => {
    switch (whichSystem) {
      case ROMAN_SYSTEM:
        return romanTutorialProgress;
      case BINARY_SYSTEM:
        return binaryTutorialProgress;
      case MAYA_SYSTEM:
        return mayaTutorialProgress;
      default:
        console.log("tutorialProgress: invalid system code passed to tutorialProgress()!");
    }
  };

  const setTutorialProgress = (whichSystem, newProgress) => {
    switch (whichSystem) {
      case ROMAN_SYSTEM:
        setRomanTutorialProgress(newProgress);
        break;
      case BINARY_SYSTEM:
        setBinaryTutorialProgress(newProgress);
        break;
      case MAYA_SYSTEM:
        setMayaTutorialProgress(newProgress);
        break;
      default:
        console.log("setTutorialProgress: invalid system code passed to setTutorialProgress()!");
    }
  };

  const numberOfLevels = (whichSystem) => {
    switch (whichSystem) {
      case ROMAN_SYSTEM:
        return ROMAN_NUMBER_OF_LEVELS;
      case BINARY_SYSTEM:
        return BINARY_NUMBER_OF_LEVELS;
      case MAYA_SYSTEM:
        return MAYA_NUMBER_OF_LEVELS;
      default:
        console.log("numberOfLevels: invalid system code passed to numberOflevels()!");
    }
  };

  /**
   * 
   * @param {Number} whichSystem 
   * @returns Array containing a number for each level of the chosen system. That number indicates how much progress is made in the respective level.
   */
  const levelProgress = (whichSystem) => {
    switch (whichSystem) {
      case ROMAN_SYSTEM:
        return romanLevelProgress;
      case BINARY_SYSTEM:
        return binaryLevelProgress;
      case MAYA_SYSTEM:
        return mayaLevelProgress;
      default:
        console.log("levelProgress: invalid system code passed to levelProgress()!");
    }
  };

  /**
   * Changes levelProgress pointer to newLevelProgress. Does not change any contents. Does not create a new array.
   * @param {int} whichSystem 
   * @param {Array} newLevelProgress 
   * @returns 
   */
  const setLevelProgress = (whichSystem, newLevelProgress) => {
    switch (whichSystem) {
      case ROMAN_SYSTEM:
        return setRomanLevelProgress(newLevelProgress);
      case BINARY_SYSTEM:
        return setBinaryLevelProgress(newLevelProgress);
      case MAYA_SYSTEM:
        return setMayaLevelProgress(newLevelProgress);
      default:
        console.log("setLevelProgress: invalid system code passed to setLevelProgress()!");
    }
  };
  /**
   * Resets all progress. Starts new game.
   */
  const reset = (() => {
      setProgress(ROMAN_SYSTEM, 0);
      setProgress(BINARY_SYSTEM, 0);
      setProgress(MAYA_SYSTEM, 0);
      setLevel(0);
      const newRomanProgress = [...levelProgress(ROMAN_SYSTEM)]; // Copy structure.
      const newBinaryProgress = [...levelProgress(BINARY_SYSTEM)];
      const newMayaProgress = [...levelProgress(MAYA_SYSTEM)];
      // Explicitly deleting and creating a new array for each system, because otherwise they might point to the same array.
      for(let i=0; i<numberOfLevels(ROMAN_SYSTEM); i++){
        newRomanProgress[i] = 0;
      }
      for(let i=0; i<numberOfLevels(BINARY_SYSTEM); i++){
        newBinaryProgress[i] = 0;
      }
      for(let i=0; i<numberOfLevels(MAYA_SYSTEM); i++){
        newMayaProgress[i] = 0;
      }
      setLevelProgress(ROMAN_SYSTEM,newRomanProgress);
      setLevelProgress(BINARY_SYSTEM,newBinaryProgress);
      setLevelProgress(MAYA_SYSTEM,newMayaProgress);
      setTutorialProgress(ROMAN_SYSTEM, newRomanProgress); // Can use the same progress arrays for resetting tutorials as for resetting the levels themselves, as they have the same length for each system.
      setTutorialProgress(BINARY_SYSTEM, newBinaryProgress);
      setTutorialProgress(MAYA_SYSTEM, newMayaProgress);
      setShowIntroModal(1); // Upon reset, show intro again.
  });

  /**
   * Unlocks everything. For debugging and demonstration purposes.
   */
   const unlockAll = (() => {
    setProgress(ROMAN_SYSTEM,ROMAN_NUMBER_OF_LEVELS-1);
    setProgress(BINARY_SYSTEM, BINARY_NUMBER_OF_LEVELS-1);
    setProgress(MAYA_SYSTEM, MAYA_NUMBER_OF_LEVELS-1);
    setLevel(0); // Safest to just set to level 0.
    const newRomanProgress = [...levelProgress(ROMAN_SYSTEM)]; // Copy structure.
    const newBinaryProgress = [...levelProgress(BINARY_SYSTEM)];
    const newMayaProgress = [...levelProgress(MAYA_SYSTEM)];
    // Explicitly deleting and creating a new array for each system, because otherwise they might point to the same array.
    for(let i=0; i<numberOfLevels(ROMAN_SYSTEM); i++){
      newRomanProgress[i] = 3;
    }
    for(let i=0; i<numberOfLevels(BINARY_SYSTEM); i++){
      newBinaryProgress[i] = 3;
    }
    for(let i=0; i<numberOfLevels(MAYA_SYSTEM); i++){
      newMayaProgress[i] = 3;
    }
    setLevelProgress(ROMAN_SYSTEM,newRomanProgress);
    setLevelProgress(BINARY_SYSTEM,newBinaryProgress);
    setLevelProgress(MAYA_SYSTEM,newMayaProgress);
    setTutorialProgress(ROMAN_SYSTEM, newRomanProgress); // Can use the same progress arrays for setting tutorials as for setting the levels themselves, as they have the same length for each system.
    setTutorialProgress(BINARY_SYSTEM, newBinaryProgress);
    setTutorialProgress(MAYA_SYSTEM, newMayaProgress);
    setShowIntroModal(0); // Do not show intro again.
    });
  

   // ChooseSystemPopup definitions:
   const [showChooseModal, setShowChooseModal] = useState(false);
   const hideChooseModal = () => {
     closeAllPopups(); // Make completely sure that all popups are closed before starting the game.
     setShowChooseModal(false);
   };
   const displayChangeSystemPopup = () => {
    closeAllPopups(); // Close all popups when entering change-system screen.
    setShowChooseModal(true);
   }
   
   
   // Introduction definitions:
   const [showIntroModal, setShowIntroModal] = useLocalStorage("showIntro", 1); // Local storage because this variable should persist across restarts. Number because Booleans are buggy in local storage.
   const hideIntroModal = () => {
     setShowIntroModal(0);
   };


   // NewGamePopup definitions:
   const [showNewGameModal, setShowNewGameModal] = useState(false);
   const hideNewGameModal = () => {
     setShowNewGameModal(false);
   };
   const startNewGame = () => {
     reset();
     hideNewGameModal();
   };

   // InformationPopup definitions:
   const [showInfoModal, setShowInfoModal] = useState(false);
   const hideInfoModal = () => {
    setShowInfoModal(false);
   };
   const displayInfoModal = () => {
    setShowInfoModal(true);
   };
   
   // DraggableMessage definition:
   const [showMessage, setShowMessage] = useState(false);


   // DraggableLegend definitions:
   const [showLegend, setShowLegend] = useState(false);
   const displayLegend = () => {
    setShowLegend(true);
   };


  // DraggableLevelUnlocked definition:
  const [showLevelUnlocked, setShowLevelUnlocked] = useState(false);
  const displayLevelUnlocked = () => {
    setShowLevelUnlocked(true);
  };

  // Show DraggableLevelUnlocked every time the appropriate progress increases, i.e. a new level is unlocked. Note that progress only ever changes after the message popup has been closed (so showMessage is an implicit dependency).
  // Note that this effect also triggers upon calling unlockAll() because that function changes progress. unlockAll() will, however, not be callable in production (debug only), so this is fine.
  useEffect(() => {
    setDisableInput(true);
    displayLevelUnlocked();
  }, [progress(whichSystem)]);

  // Re-enable input when LevelUnlocked is closed.
  useEffect(() => {
    if (!showLevelUnlocked && !showMessage && disableInput) { // In particular: Re-enable input after LevelUnlocked is closed AND no message is displayed (because then it should stay disabled) AND it currently is disabled.
      setDisableInput(false);
    }
  }, [showLevelUnlocked]);


  // DraggableTutorial definitions:
  const [showTutorial, setShowTutorial] = useState(false);
  const displayTutorial = () => {
    setShowTutorial(true);
  };

  useEffect(() => {
     // Show tutorial every time a new level is loaded for which the tutorial has not yet been completed. Close all other popups.
    if (tutorialProgress(whichSystem)[level] === 0 && !showChooseModal && !showIntroModal && !showNewGameModal) { // Do not show tutorial if in any of these screens.
      closeAllPopups();
      setDisableInput(true); // Disable input while tutorial is shown for the first time. But only the first time.
      displayTutorial();
    }
    // When loading into level for which the tutorial has been completed, just close all popups.
    else if (tutorialProgress(whichSystem)[level] === 1) {
      closeAllPopups();
    }
  }, [level, showChooseModal, showIntroModal, showNewGameModal]);

  // Re-enable input after tutorial is completed.
  useEffect(() => {
    if (!showTutorial && !showMessage && disableInput) { // In particular: Re-enable input after tutorial is closed AND no message is displayed (because then it should stay disabled) AND it currently is disabled.
      setDisableInput(false);
    }
  }, [showTutorial]);


  // Close all popups.
  const closeAllPopups = () => {
    setShowLegend(false);
    setShowTutorial(false);
    setShowMessage(false);
    setShowLevelUnlocked(false);
    setDisableInput(false); // Make sure to reset everything.
  };

  useEffect(() => {
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  const goPreviousLevel = () => {
    if (level > 0) {
      setLevel((prev) => prev - 1);
    }
  };

  const goNextLevel = () => {
    if (level < progress(whichSystem)) {
      setLevel((prev) => prev + 1);
    }
  };

  // LLM-generated useEffect. Runs code in if-block everytime a new session starts.
  useEffect(() => {
    // Check if the "visited" key exists in sessionStorage
    const visited = sessionStorage.getItem('visited');

    if (!visited) {
      // Set the "visited" key in sessionStorage so it won't trigger again during the session. sessionStorage is cleared upon closing browser or tab.
      sessionStorage.setItem('visited', 'true');
      setShowNewGameModal(true); // Triggered everytime a new session starts (e.g. reloaded tab or browser).
      setShowChooseModal(true);
    }
  }, []);

  const noSaveAvailable = [0,1,2].every((whichSystem) => (progress(whichSystem) === 0 && tutorialProgress(whichSystem).every((value) => value === 0))); // No save is available if 1. progress for every level in every system is 0 and 2. the entire tutorialProgress array is 0 for every system.
  const hasCompletedGame = [0,1,2].every((whichSystem) => progress(whichSystem) === numberOfLevels(whichSystem));

  return (
    <div className="App">
      <header className="App-header" style={{ backgroundColor: COLOR_LIGHT }}>
        {isScreenSmall ? (
          <h1 style={{ fontFamily: FONT }}>{AppScreenSize}</h1>
        ) : isScreenPortrait ? (
          <h1 style={{ fontFamily: FONT }}>{AppRotateScreen}</h1>
        ) : (
          <>
            <div className="Level-label" style={{ fontFamily: FONT }}>
            </div>
            <button
              onClick={displayTutorial}
              className="show-tutorial-button"
            >
              {buttonShowTutorial}
            </button>
            <button
              style={{fontSize: lang === 2 ? "1.1dvh" : undefined}} // Scale down font for French language.
              onClick={displayChangeSystemPopup}
              className="change-system-button"
            >
              {buttonChangeSystem}
            </button>
            {whichSystem === ROMAN_SYSTEM && <img src={mascot_roman} style={{position: "absolute", top: "3.2dvh", left: "35dvh", height: "9.4dvh", width: "4.7dvh", filter: "drop-shadow(0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5))"}}/>}
            {whichSystem === BINARY_SYSTEM && <img src={mascot_computer} style={{position: "absolute", top: "3.2dvh", left: "35dvh", height: "6.9dvh", width: "6.9dvh", filter: "drop-shadow(0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5))"}}/>}
            {whichSystem === MAYA_SYSTEM && <img src={mascot_maya} style={{position: "absolute", top: "3.2dvh", left: "35dvh", height: "9.4dvh", width: "4.7dvh", filter: "drop-shadow(0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5))"}}/>}
            {whichSystem !== BINARY_SYSTEM && // Do not render button for binary system, as it has no need for a legend.
            <button
              style={{fontSize: lang === 2 ? "1.1dvh" : undefined}} // Scale down font for French language.
              onClick={displayLegend}
              className="show-legend-button"
            >
              {buttonShowLegend}
            </button>
            }
            <button
            onClick={unlockAll}
            className="unlock-all-button">
              DEBUG: UNLOCK ALL
            </button>
            <button
            onClick={reset}
            className="reset-button">
              DEBUG: RESET
            </button>
            {level > 0 && (
              <button
                onClick={goPreviousLevel}
                className="navigation-arrow"
                style={{
                  left: "3vw",
                }}
              >
                <img src={arrowLeft} style={{width: "7dvh", height: "7dvh"}}/>
              </button>
            )}
           
            <div
              className="Task"
            >
              <Level
                level={level}
                setLevel={setLevel}
                progress={progress}
                setProgress={setProgress}
                levelProgress={levelProgress}
                setLevelProgress={setLevelProgress}
                tutorialProgress={tutorialProgress}
                setTutorialProgress={setTutorialProgress}
                showMessage={showMessage}
                setShowMessage={setShowMessage}
                disableInput={disableInput}
                setDisableInput={setDisableInput}
                whichSystem={whichSystem}
                numberOfLevels={numberOfLevels}
              />
            </div>
            {/* Order of popups is determined by the z-index in their respective styles. */}
            <ChooseSystemPopup show={showChooseModal} handleClose={hideChooseModal} setWhichSystem={setWhichSystem} displayInfoModal={displayInfoModal} progress={progress} setLevel={setLevel}> 
            </ChooseSystemPopup>
            <Introduction show={showIntroModal} handleClose={hideIntroModal}>
            </Introduction>
            <NewGamePopup 
            show={showNewGameModal} handleCloseContinue={hideNewGameModal} handleCloseNewGame={startNewGame} displayInfoModal={displayInfoModal} noSaveAvailable={noSaveAvailable}>
            </NewGamePopup>
            <DndProvider options={HTML5toTouch}>
              {showLegend && <DraggableLegend show={showLegend} setShow={setShowLegend} whichSystem={whichSystem} currentLevel={level}/>} {/* Conditional rendering resets position of legend every time it is rendered. */}
              {showTutorial && <DraggableTutorial show={showTutorial} setShow={setShowTutorial} whichSystem={whichSystem} currentLevel={level} tutorialProgress={tutorialProgress} setTutorialProgress={setTutorialProgress}/>} {/* Conditional rendering resets position of legend every time it is rendered. */}
              {showLevelUnlocked && 
                <DraggableLevelUnlocked 
                  show={showLevelUnlocked} 
                  setShow={setShowLevelUnlocked} 
                  isLastLevel={numberOfLevels(whichSystem)-1 === level} 
                  isOneExercise={whichSystem === BINARY_SYSTEM && (level === 4 || level === 5)} 
                  hasCompletedGame={hasCompletedGame}
                  setLevel={setLevel}
                  changeSystem={displayChangeSystemPopup}
                />
              }
            </DndProvider>
            <InformationPopup show={showInfoModal} handleClose={hideInfoModal}>
            </InformationPopup>
            {level < progress(whichSystem) && level < numberOfLevels(whichSystem)-1 && (
              <button
                onClick={goNextLevel}
                className="navigation-arrow"
                style={{
                  right: "3vw",
                }}
              >
                <img src={arrowRight} style={{width: "7dvh", height: "7dvh"}}/>
              </button>
            )}
            <div style={{ display: "flex", width: "87%", marginLeft: "13%" }}>
              <Navbar level={level} setLevel={setLevel} progress={progress} levelProgress={levelProgress} whichSystem={whichSystem} numberOfLevels={numberOfLevels} />
            </div>
          </>
        )}
      </header>
    </div>
  );
}

export default App;
